import { useState } from 'react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Cat, Plus, ChevronLeft, ChevronRight, ClipboardList, Coins, Send, Settings, GraduationCap, Star, CalendarIcon } from 'lucide-react';
import dynamicIconImports from 'lucide-react/dynamicIconImports';
import { lazy, Suspense } from 'react';
import { User } from 'lucide-react';
import { useUserIcons } from '@/hooks/useUserIcons';
import { useSchoolHolidays } from '@/hooks/useSchoolHolidays';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';

interface MobileCalendarHeaderProps {
  currentTime: Date;
  selectedDay: Date;
  selectedDayIndex: number;
  weekOffset: number;
  weekDays: Date[];
  selectedUsers: string[];
  matanBalance: { balance: number } | undefined;
  isUserAdmin: boolean;
  onNavigateDay: (direction: 'prev' | 'next') => void;
  onSelectDay: (index: number) => void;
  onGoToToday: () => void;
  onToggleUser: (userId: string) => void;
  onOpenUserSettings: (userId: string) => void;
  onOpenManagement: () => void;
  
  onOpenAddEvent: () => void;
  onOpenCoinHistory: () => void;
  onTransferCoins: () => void;
  onNavigateToTasks: () => void;
  onGoToDate: (date: Date) => void;
  transferCoinsPending: boolean;
}

const USERS = [
  { id: 'matan', name: 'מתן', color: 'hsl(var(--skylight-blue))', textColor: 'text-skylight-blue' },
  { id: 'heila', name: 'הילה', color: 'hsl(var(--skylight-pink))', textColor: 'text-skylight-pink' },
  { id: 'asaf', name: 'אסף', color: 'hsl(var(--skylight-green))', textColor: 'text-skylight-green' }
];

const DAY_NAMES = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];

const toKebabCase = (str: string) => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
};

function DynamicIcon({ iconName, className, style }: { iconName: string; className?: string; style?: React.CSSProperties }) {
  const kebabName = toKebabCase(iconName);
  const iconImport = dynamicIconImports[kebabName as keyof typeof dynamicIconImports];
  
  if (!iconImport) {
    return <User className={className} style={style} />;
  }
  
  const LucideIcon = lazy(iconImport);
  
  return (
    <Suspense fallback={<User className={className} style={style} />}>
      <LucideIcon className={className} style={style} />
    </Suspense>
  );
}

export function MobileCalendarHeader({
  currentTime,
  selectedDay,
  selectedDayIndex,
  weekOffset,
  weekDays,
  selectedUsers,
  matanBalance,
  isUserAdmin,
  onNavigateDay,
  onSelectDay,
  onGoToToday,
  onToggleUser,
  onOpenUserSettings,
  onOpenManagement,
  
  onOpenAddEvent,
  onOpenCoinHistory,
  onTransferCoins,
  onNavigateToTasks,
  onGoToDate,
  transferCoinsPending
}: MobileCalendarHeaderProps) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const { getUserIcon } = useUserIcons();
  const { getHolidayForDate, getIsraeliHolidayForDate } = useSchoolHolidays();
  const isToday = weekOffset === 0 && selectedDayIndex === new Date().getDay();
  const currentHoliday = getHolidayForDate(selectedDay);
  const currentIsraeliHoliday = getIsraeliHolidayForDate(selectedDay);

  return (
    <div className="bg-slate-800 border-b border-slate-700 px-3 py-2 space-y-2 sticky top-0 z-20">
      {/* Row 1: Date, Time, Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-200">
            {format(currentTime, 'HH:mm')}
          </span>
          <span className="text-xs text-slate-400">
            {format(currentTime, 'd MMM', { locale: he })}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <Button 
            size="sm"
            className="bg-skylight-blue hover:bg-skylight-blue/90 text-white rounded-full h-8 w-8 p-0"
            onClick={onOpenAddEvent}
          >
            <Plus className="h-4 w-4" />
          </Button>

          {isUserAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onOpenManagement}
              className="h-8 w-8 p-0 text-slate-300"
              title="ניהול משימות"
            >
              <ClipboardList className="h-4 w-4" />
            </Button>
          )}

          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-slate-300"
                title="בחר תאריך"
              >
                <CalendarIcon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={selectedDay}
                onSelect={(date) => {
                  if (date) {
                    onGoToDate(date);
                    setCalendarOpen(false);
                  }
                }}
                initialFocus
                className="p-3"
                modifiers={{
                  schoolHoliday: (date) => getHolidayForDate(date) !== null,
                  israeliHoliday: (date) => getIsraeliHolidayForDate(date) !== null,
                }}
                modifiersClassNames={{
                  schoolHoliday: "bg-amber-500/20 text-amber-400 font-medium",
                  israeliHoliday: "bg-violet-500/20 text-violet-400 font-medium",
                }}
              />
            </PopoverContent>
          </Popover>

          <Button
            variant="ghost"
            size="sm"
            onClick={onNavigateToTasks}
            className="h-8 w-8 p-0 text-slate-300"
          >
            <Cat className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Row 2: Day Navigation */}
      <div className="flex items-center justify-between">
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 text-slate-300"
          onClick={() => onNavigateDay('prev')}
        >
          <ChevronRight className="h-5 w-5" />
        </Button>

        <div className="flex items-center gap-1">
          {DAY_NAMES.map((name, index) => {
            const isSelected = selectedDayIndex === index;
            const isTodayDot = weekOffset === 0 && index === new Date().getDay();
            const dayDate = weekDays[index];
            const dayHoliday = dayDate ? getHolidayForDate(dayDate) : null;
            const dayIsraeliHoliday = dayDate ? getIsraeliHolidayForDate(dayDate) : null;
            
            return (
              <button
                key={index}
                onClick={() => onSelectDay(index)}
                className={cn(
                  "w-8 h-8 rounded-full text-xs font-medium transition-all relative",
                  isSelected
                    ? "bg-skylight-blue text-white"
                    : dayHoliday
                      ? "text-amber-400 hover:bg-slate-700"
                      : dayIsraeliHoliday
                        ? "text-violet-400 hover:bg-slate-700"
                        : "text-slate-400 hover:bg-slate-700"
                )}
                title={dayHoliday?.name || dayIsraeliHoliday?.name}
              >
                {name}
                {dayHoliday && !isSelected && (
                  <GraduationCap className="absolute -top-0.5 -right-0.5 w-3 h-3 text-amber-400" />
                )}
                {dayIsraeliHoliday && !dayHoliday && !isSelected && (
                  <Star className="absolute -top-0.5 -right-0.5 w-3 h-3 text-violet-400" />
                )}
                {isTodayDot && !isSelected && !dayHoliday && !dayIsraeliHoliday && (
                  <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-skylight-blue" />
                )}
              </button>
            );
          })}
        </div>

        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 text-slate-300"
          onClick={() => onNavigateDay('next')}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
      </div>

      {/* Row 3: Selected Day Display */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-slate-100">
              {format(selectedDay, 'EEEE', { locale: he })}
            </span>
            <span className="text-sm text-slate-400">
              {format(selectedDay, 'd/M')}
            </span>
          </div>
          
          {currentHoliday && (
            <div className="flex items-center gap-1.5 text-amber-400">
              <GraduationCap className="h-4 w-4" />
              <span className="text-sm font-medium">{currentHoliday.name}</span>
            </div>
          )}
          {currentIsraeliHoliday && (
            <div className="flex items-center gap-1.5 text-violet-400">
              <Star className="h-4 w-4" />
              <span className="text-sm font-medium">{currentIsraeliHoliday.name}</span>
            </div>
          )}
        </div>
        
        {!isToday && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onGoToToday}
            className="text-xs text-skylight-blue h-6"
          >
            היום
          </Button>
        )}
      </div>

      {/* Row 4: User Filters */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-3 px-3 scrollbar-hide">
        {USERS.map(user => (
          <div 
            key={user.id} 
            className="flex items-center gap-1 shrink-0"
          >
            <button
              onClick={() => onToggleUser(user.id)}
              className={cn(
                "flex items-center gap-1.5 px-2 py-1 rounded-full text-xs transition-all",
                selectedUsers.includes(user.id) 
                  ? "bg-slate-600 text-slate-200" 
                  : "bg-transparent opacity-40 text-slate-400"
              )}
            >
              {getUserIcon(user.id) ? (
                <DynamicIcon 
                  iconName={getUserIcon(user.id)!} 
                  className="w-3.5 h-3.5" 
                  style={{ color: user.color }}
                />
              ) : (
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: user.color }}
                />
              )}
              <span>{user.name}</span>
            </button>
            
            {/* Coin balance for Matan */}
            {user.id === 'matan' && matanBalance && selectedUsers.includes(user.id) && (
              <button
                onClick={onOpenCoinHistory}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold"
              >
                <Coins className="w-3 h-3" />
                <span>{matanBalance.balance}</span>
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
